import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DataTable,
  type DataTableColumn,
  type DataTableMobileRenderContext,
} from '@/components/ui/data-table';
import { FilterChips } from '@/components/ui/filter-chips';
import { NurseryPriceEditor } from '@/components/NurseryPriceEditor';
import { VariantFieldCell } from '@/components/VariantFieldCell';
import { VariantInventoryCell } from '@/components/VariantInventoryCell';
import { ProductStatusBadge } from '@/components/StatusBadge';
import { MobileMetric } from '@/components/MobileMetric';
import { OnlinePrice, PriceDiff } from '@/components/PriceCells';
import { BulkActionBar } from '@/components/bulk/BulkActionBar';
import { BulkEditActions } from '@/components/bulk/BulkEditActions';
import type { BulkTargets } from '@/components/bulk/types';
import { useVariants } from '@/hooks/useVariants';
import { useRowSelection } from '@/hooks/useRowSelection';
import { usePagination } from '@/hooks/usePagination';
import { variantOptionLabel } from '@/lib/utils';
import type { VariantRow } from '@/types';

function VariantMobileCard({
  item,
  context,
}: {
  item: VariantRow;
  context: DataTableMobileRenderContext;
}) {
  const option = variantOptionLabel(item);
  const hasOption = option !== 'Default';
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        {context.selectable && (
          <div data-no-row-click className="pt-1" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={context.isSelected}
              onCheckedChange={context.toggleSelected}
              aria-label={`Select ${item.product_title}`}
            />
          </div>
        )}
        {item.image_url ? (
          <img
            src={item.image_url}
            alt=""
            className="h-14 w-14 shrink-0 rounded-md bg-muted object-cover"
          />
        ) : (
          <div className="h-14 w-14 shrink-0 rounded-md bg-muted" />
        )}
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-sm font-medium leading-5 text-foreground">
            {item.product_title}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {hasOption && (
              <Badge variant="outline" className="text-[10px]">
                {option}
              </Badge>
            )}
            <ProductStatusBadge status={item.product_status} className="text-[10px]" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MobileMetric label="Online">
          <OnlinePrice
            price={item.online_price}
            compareAtPrice={item.online_compare_at_price}
            isOnSale={item.is_on_sale}
          />
        </MobileMetric>
        <MobileMetric label="Nursery">
          <NurseryPriceEditor
            shopifyVariantId={item.shopify_variant_id}
            value={item.nursery_price}
          />
        </MobileMetric>
        <MobileMetric label="Qty">
          <VariantInventoryCell
            shopifyVariantId={item.shopify_variant_id}
            shopifyInventoryItemId={item.shopify_inventory_item_id}
            value={item.inventory_quantity}
          />
        </MobileMetric>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <div data-no-row-click onClick={(e) => e.stopPropagation()} className="min-w-0 flex-1">
          <VariantFieldCell
            shopifyVariantId={item.shopify_variant_id}
            shopifyProductId={item.shopify_product_id}
            field="sku"
            value={item.sku}
          />
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {item.product_type ?? 'No type'}
        </span>
      </div>
    </div>
  );
}

const columns: DataTableColumn<VariantRow>[] = [
  {
    key: 'product_title',
    label: 'Variant',
    sortable: true,
    className: 'max-w-[320px]',
    render: (item) => {
      const option = variantOptionLabel(item);
      const hasOption = option !== 'Default';
      return (
        <div className="flex min-w-0 items-center gap-3">
          {item.image_url ? (
            <img
              src={item.image_url}
              alt=""
              className="h-8 w-8 shrink-0 rounded bg-muted object-cover"
            />
          ) : (
            <div className="h-8 w-8 shrink-0 rounded bg-muted" />
          )}
          <div className="min-w-0">
            <p className="truncate text-sm text-foreground">{item.product_title}</p>
            {hasOption && (
              <p className="truncate text-[10px] text-muted-foreground">{option}</p>
            )}
          </div>
        </div>
      );
    },
  },
  {
    key: 'sku',
    label: 'SKU',
    sortable: true,
    className: 'w-[160px]',
    render: (item) => (
      <VariantFieldCell
        shopifyVariantId={item.shopify_variant_id}
        shopifyProductId={item.shopify_product_id}
        field="sku"
        value={item.sku}
      />
    ),
  },
  {
    key: 'product_type',
    label: 'Type',
    sortable: true,
    className: 'w-[120px]',
    render: (item) => (
      <span className="truncate text-xs text-muted-foreground">{item.product_type ?? '—'}</span>
    ),
  },
  {
    key: 'online_price',
    label: 'Online',
    sortable: true,
    className: 'w-[150px]',
    render: (item) => (
      <OnlinePrice
        price={item.online_price}
        compareAtPrice={item.online_compare_at_price}
        isOnSale={item.is_on_sale}
      />
    ),
  },
  {
    key: 'nursery_price',
    label: 'Nursery',
    sortable: true,
    className: 'w-[100px]',
    render: (item) => (
      <NurseryPriceEditor shopifyVariantId={item.shopify_variant_id} value={item.nursery_price} />
    ),
  },
  {
    key: 'price_diff',
    label: 'Diff',
    sortable: true,
    className: 'w-[80px]',
    render: (item) => <PriceDiff online={item.online_price} nursery={item.nursery_price} />,
  },
  {
    key: 'inventory_quantity',
    label: 'Qty',
    sortable: true,
    className: 'w-[96px]',
    render: (item) => (
      <VariantInventoryCell
        shopifyVariantId={item.shopify_variant_id}
        shopifyInventoryItemId={item.shopify_inventory_item_id}
        value={item.inventory_quantity}
      />
    ),
  },
  {
    key: 'product_status',
    label: 'Status',
    sortable: true,
    className: 'w-[90px]',
    render: (item) => <ProductStatusBadge status={item.product_status} className="text-[10px]" />,
  },
];

const STATUS_CHIPS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

const PRICE_CHIPS = [
  { value: 'all', label: 'All prices' },
  { value: 'on_sale', label: 'On sale' },
  { value: 'no_nursery', label: 'Missing nursery' },
  { value: 'nursery_lower', label: 'Nursery lower' },
  { value: 'nursery_higher', label: 'Nursery higher' },
  { value: 'same', label: 'Same price' },
];

/**
 * The Variants list — one row per variant. This is where bulk variant editing
 * happens with true per-variant precision: check exactly the variants you want,
 * then use the floating action bar. (The Products list shows the same data
 * grouped by product, with expandable variant rows.)
 */
export function VariantsPage() {
  const navigate = useNavigate();
  const {
    page,
    perPage,
    search,
    sort,
    setPage,
    setSearch,
    setSort,
    getSearchPath,
  } = usePagination({
    initialSort: { field: 'product_title', direction: 'asc' },
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');
  const selection = useRowSelection<VariantRow>((r) => r.id);

  const { data, isLoading, isFetching } = useVariants({
    page,
    per_page: perPage,
    search,
    sort_by: sort.field,
    sort_dir: sort.direction,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    price_comparison: priceFilter !== 'all' ? priceFilter : undefined,
  });

  const rows = data?.data ?? [];

  const handleStatusFilter = useCallback(
    (value: string) => {
      setStatusFilter(value);
      setPage(1);
    },
    [setPage],
  );
  const handlePriceFilter = useCallback(
    (value: string) => {
      setPriceFilter(value);
      setPage(1);
    },
    [setPage],
  );

  const handleRowClick = useCallback(
    (item: VariantRow) =>
      navigate(getSearchPath(`/products/${item.shopify_product_id}`)),
    [getSearchPath, navigate],
  );

  const filtersActive = statusFilter !== 'all' || priceFilter !== 'all';

  const filterBar = (
    <div className="flex min-w-0 flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-x-4 md:gap-y-2">
      <FilterChips
        options={STATUS_CHIPS}
        value={statusFilter}
        onChange={handleStatusFilter}
        className="w-full md:w-auto"
      />
      <FilterChips
        options={PRICE_CHIPS}
        value={priceFilter}
        onChange={handlePriceFilter}
        className="w-full md:w-auto"
      />
      {filtersActive && (
        <Button
          variant="ghost"
          size="sm"
          className="h-9 justify-center text-xs md:h-7"
          onClick={() => {
            setStatusFilter('all');
            setPriceFilter('all');
            setPage(1);
          }}
        >
          Clear filters
        </Button>
      )}
    </div>
  );

  const selectedRows = selection.getSelectedRows();
  const targets: BulkTargets = {
    selectionLabel: `${selection.count} ${selection.count === 1 ? 'variant' : 'variants'}`,
    productShopifyIds: [...new Set(selectedRows.map((r) => r.shopify_product_id))],
    resolveVariants: async () => selection.getSelectedRows(),
  };

  return (
    <div className="flex h-full flex-col">
      <div className="min-h-0 flex-1">
        <DataTable
          columns={columns}
          data={rows}
          total={data?.total ?? 0}
          page={page}
          perPage={perPage}
          sort={sort}
          search={search}
          isLoading={isLoading}
          isFetching={isFetching}
          selectable
          selectedIds={selection.selectedIds}
          getRowId={(item) => item.id}
          onPageChange={setPage}
          onSortChange={setSort}
          onSearchChange={setSearch}
          onSelectionChange={(next) => selection.setSelection(next, rows)}
          onRowClick={handleRowClick}
          searchPlaceholder="Search by product, variant, SKU, barcode..."
          emptyMessage="No variants found"
          emptySubMessage="Sync your Shopify catalog or adjust your search"
          filterBar={filterBar}
          mobileRender={(item, context) => (
            <VariantMobileCard item={item} context={context} />
          )}
        />
      </div>

      {/* Reserve space so the floating bar never covers the pagination footer. */}
      {selection.count > 0 && <div className="h-[80px] shrink-0 md:h-[88px]" />}

      <BulkActionBar count={selection.count} itemNoun="variant" onClear={selection.clear}>
        <BulkEditActions targets={targets} onApplied={selection.clear} />
      </BulkActionBar>
    </div>
  );
}
